import { parseSignOnSavePrefs, type SignOnSavePreferences } from '@/shared/lib/signOnSavePrefs'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import { getCurrentUserId, toJsonObject } from './userSettingsCommon'
import { supabase } from '@/shared/supabase/client'
import type { Json } from '@/shared/supabase/database.types'
import { AppError } from '@/shared/lib/appError'
import { runSerializedUserSettingsOperation } from './userSettingsWriteQueue'

const assertCurrentUser = async (expectedUserId: string): Promise<void> => {
  if (await getCurrentUserId() !== expectedUserId) {
    throw new AppError('Sessão alterada. Tente novamente.', { status: 401, code: 'UNAUTHORIZED' })
  }
}

/** Preferências de solicitar assinatura: altera só a chave signOnSaveByType. */
export const signOnSavePrefsRepository = {
  async getSignOnSavePreferences(expectedUserId?: string): Promise<SignOnSavePreferences> {
    if (isScreenshotFixtureMode()) return parseSignOnSavePrefs(null)
    try {
      const userId = expectedUserId ?? await getCurrentUserId()
      return await runSerializedUserSettingsOperation(userId, async () => {
        await assertCurrentUser(userId)
        const { data, error } = await supabase.from('user_settings')
          .select('chart_preferences').eq('user_id', userId).maybeSingle()
        if (error) throw error
        return parseSignOnSavePrefs(toJsonObject(data?.chart_preferences).signOnSaveByType)
      })
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível carregar as preferências de assinatura ao salvar.')
    }
  },

  async updateSignOnSavePreferences(payload: Partial<SignOnSavePreferences>, expectedUserId?: string): Promise<void> {
    if (isScreenshotFixtureMode()) return
    try {
      const userId = expectedUserId ?? await getCurrentUserId()
      await runSerializedUserSettingsOperation(userId, async () => {
        await assertCurrentUser(userId)
        const { data, error } = await supabase.from('user_settings')
          .select('chart_preferences').eq('user_id', userId).maybeSingle()
        if (error) throw error
        const current = toJsonObject(data?.chart_preferences)
        const previous = toJsonObject(current.signOnSaveByType)
        const next = parseSignOnSavePrefs({ ...previous, ...payload })
        const chartPreferences: Json = {
          ...current,
          signOnSaveByType: { ...previous, ...next },
        }
        await assertCurrentUser(userId)
        const { error: saveError } = await supabase.from('user_settings').upsert(
          { user_id: userId, chart_preferences: chartPreferences },
          { onConflict: 'user_id' },
        )
        if (saveError) throw saveError
      })
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível salvar as preferências de assinatura ao salvar.')
    }
  },
}
